import { TeamMemberRoleIds } from './selectLists/teamMemberRoles'
import { makeGetRouteForLastSelectedTab } from './factories'
import { httpGet, httpPost, httpPatchMerge, httpDelete } from './http'
import { isSystemPerson } from './persons'
import type { TEntityId } from '~/types/models'
import type {
  ITeamMember,
  ITeamMemberOverview,
  ITeamMemberLastSelectedState,
} from '~/types/teamMember/models'
import { TeamMember } from '~/cynqueModules/dataObjects/TeamMember'

const apiEntityPath = '/api/teammembers'

export async function getTeamMembers({ apiPath = apiEntityPath } = {}) {
  const callingMn = getTeamMembers.name
  return httpGet<ITeamMember[]>({ callingMn, apiPath })
}

/**
 * Is this a Cynque bot -- one of the identities stamped as the author on rows
 * Cynque creates itself? Case Bot writes the email-ingested case notes and
 * documents; expect one per automated writer. See WXTG/cynque-api-server#1600,
 * which supersedes the nullable-author approach in #1446: giving the automated
 * writer an identity keeps "every note has an author" an enforced invariant, and
 * says *which* non-human wrote it, which a NULL cannot.
 *
 * 🔑 Read from the PERSON, which is where the flag lives. A team member is a
 * binding of a person into a role, and "not a human" is a fact about the
 * identity, so every binding of it -- team member, user, contact -- inherits the
 * one flag. The server embeds the whole person in each binding (a `composed`
 * embed), so this costs no lookup. A second copy on the team member row could
 * disagree with the person's, and nothing would catch that.
 *
 * ⚠ Keyed on the flag, not on a well-known id. An id has to be agreed across
 * environments and silently means "no match" where it has not been seeded -- and
 * it could only ever name one bot, where the flag covers every one of them.
 */
export function isSystemTeamMember(teamMember: ITeamMember): boolean {
  return !!teamMember.person && isSystemPerson(teamMember.person)
}

/**
 * May this team member be OFFERED as a choice?
 *
 * Two independent reasons not to be, deliberately kept apart:
 *  - the PERSON's `isSystem` -- not a human at all (a Cynque bot).
 *    Server-set and immutable.
 *  - `excludeFromTeamMemberLists` -- a person who should not be assigned new
 *    work: departed, or kept only for history. A user checkbox on the team
 *    member form, and a fact about this binding rather than about them.
 *
 * This is the single chokepoint. getTeamMembersPreProcessed and the two
 * role-scoped getters below all filter on it, and they are what the ~18
 * team-member select stanzas under entityInputFieldsService feed from, so
 * filtering here is what stops a case, task, call or meeting being assigned to
 * someone who should not receive it.
 *
 * ⚠ A row with no person reads as NOT a bot, and stays offerable. That
 * is deliberate: `person` is required by ITeamMember and FK-backed on the
 * server, so a row without one is malformed rather than automated -- and
 * TeamMember() already throws on it, loudly, in every path that maps through it.
 * Dropping it here instead would replace that with a row silently missing from a
 * picker, which is the harder failure to notice and the harder one to explain.
 */
export function isOfferableTeamMember(teamMember: ITeamMember): boolean {
  return (
    !isSystemTeamMember(teamMember) && !teamMember.excludeFromTeamMemberLists
  )
}

export async function getTeamMembersPreProcessed({
  apiPath = apiEntityPath,
} = {}) {
  const callingMn = getTeamMembers.name
  const rawTeamMembers = await httpGet<ITeamMember[]>({ callingMn, apiPath })
  return rawTeamMembers
    .filter(isOfferableTeamMember)
    .map((rawTeamMember) => TeamMember(rawTeamMember))
}

export async function getTeamMemberOverview(id: string) {
  const callingMn = getTeamMemberOverview.name
  const apiPath = `${apiEntityPath}/${id}/overview`
  const overview = await httpGet<ITeamMemberOverview>({ callingMn, apiPath })
  overview.teamMember = Object.assign(
    overview.teamMember,
    TeamMember(overview.teamMember)
  )
  return overview
}

export async function getLastSelectedStateForTeamMember(id: TEntityId) {
  const callingMn = getLastSelectedStateForTeamMember.name
  const apiPath = `${apiEntityPath}/${id}/lastselectedstate`
  return httpGet<ITeamMember>({ callingMn, apiPath })
}

export const getRouteForLastSelectedTab =
  makeGetRouteForLastSelectedTab<ITeamMemberLastSelectedState>(
    apiEntityPath,
    'Home-Overview'
  )

/**
 * The OFFERABLE team members holding a given role.
 *
 * ⚠ The role filter is not a substitute for isOfferableTeamMember. It used to be
 * assumed the principal could not reach these lists because it holds no roles --
 * but that is a property of how the row happens to be seeded (#1600 leaves its
 * `default_role` open), not a guarantee, and it says nothing at all about the
 * `excludeFromTeamMemberLists` checkbox, which these getters ignored entirely.
 *
 * 🔑 This is the only filter these two paths get: split.ts and splits.ts bind the
 * RAW getters as their `getService`, not the PreProcessed variants, so nothing
 * downstream would catch it. That makes this the line that keeps the principal
 * out of split and commission apportionment -- an explicit requirement of #1600.
 */
async function _getOfferableTeamMembersWithRole(roleId: TeamMemberRoleIds) {
  const teamMembers = await getTeamMembers()
  return teamMembers
    .filter(isOfferableTeamMember)
    .filter((teamMember) => teamMember.role?.id === roleId)
}

export async function getSalesRepTeamMembers() {
  return _getOfferableTeamMembersWithRole(TeamMemberRoleIds.SALES_REP)
}

export async function getSalesRepTeamMembersPreProcessed({
  apiPath = apiEntityPath,
} = {}) {
  const rawTeamMembers = await getSalesRepTeamMembers()
  return rawTeamMembers.map((rawTeamMember) => TeamMember(rawTeamMember))
}

export async function getSubAgentTeamMembers() {
  return _getOfferableTeamMembersWithRole(TeamMemberRoleIds.SUB_AGENT)
}

export async function getSubAgentTeamMembersPreProcessed({
  apiPath = apiEntityPath,
} = {}) {
  const rawTeamMembers = await getSubAgentTeamMembers()
  return rawTeamMembers.map((rawTeamMember) => TeamMember(rawTeamMember))
}

export async function getTeamMemberById(id: TEntityId): Promise<ITeamMember> {
  const callingMn = getTeamMemberById.name
  const apiPath = `${apiEntityPath}/${id}`
  const rawTeamMember = await httpGet<ITeamMember>({ callingMn, apiPath })
  return TeamMember(rawTeamMember)
}

export async function createTeamMember(entity: ITeamMember) {
  const callingMn = createTeamMember.name
  const apiPath = apiEntityPath
  return httpPost<ITeamMember>({ callingMn, apiPath, entity })
}

export async function updateTeamMember(
  entityData: Partial<ITeamMember>,
  id: TEntityId
) {
  const callingMn = updateTeamMember.name
  const apiPath = `${apiEntityPath}/${id}`
  type TPartialITeamMember = Partial<ITeamMember>
  return httpPatchMerge<TPartialITeamMember>({
    callingMn,
    apiPath,
    entityData,
  })
}

export async function deleteTeamMember(entityData: ITeamMember) {
  const callingMn = deleteTeamMember.name
  const apiPath = apiEntityPath
  return httpDelete({
    callingMn,
    apiPath,
    entityData,
  })
}
